import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useMercenary } from '../../contexts/MercenaryContext';
import styles from './InfirmaryBanner.module.css';

interface InfirmaryBannerProps {
  onClose?: () => void;
}

const InfirmaryBanner: React.FC<InfirmaryBannerProps> = ({ onClose }) => {
  const { mercenary } = useMercenary();
  const [timeRemaining, setTimeRemaining] = useState<string>('');
  const [battleReportId, setBattleReportId] = useState<number | null>(null);

  useEffect(() => {
    if (mercenary?.isHospitalized && mercenary.hospitalizedUntil) {
      // Fetch current hospitalization info to get battle report ID
      fetchHospitalizationInfo();
      
      const interval = setInterval(() => {
        updateTimeRemaining();
      }, 1000);

      return () => clearInterval(interval);
    }
  }, [mercenary]);

  const fetchHospitalizationInfo = async () => {
    if (!mercenary?.id) return;
    
    try {
      const response = await fetch(`/api/hospitalization/mercenary/${mercenary.id}/history`);
      const history = await response.json();
      const current = history.find((h: any) => !h.isRecovered);
      if (current?.battleReportId) {
        setBattleReportId(current.battleReportId);
      }
    } catch (error) {
      console.error('Error fetching hospitalization info:', error);
    }
  };

  const updateTimeRemaining = () => {
    if (!mercenary?.hospitalizedUntil) return;

    const now = new Date().getTime();
    const recoveryTime = new Date(mercenary.hospitalizedUntil).getTime();
    const difference = recoveryTime - now;

    if (difference > 0) {
      const hours = Math.floor(difference / (1000 * 60 * 60));
      const minutes = Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((difference % (1000 * 60)) / 1000);

      setTimeRemaining(`${hours}h ${minutes}m ${seconds}s`);
    } else {
      setTimeRemaining('Recovery complete - refresh page');
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  if (!mercenary?.isHospitalized) {
    return null;
  }

  return (
    <div className={styles.infirmaryBanner}>
      <div className={styles.bannerContent}>
        <div className={styles.icon}>INFIRMARY</div>
        <div className={styles.info}>
          <div className={styles.title}>
            <strong>{mercenary.name}</strong> is in the Infirmary
          </div>
          <div className={styles.details}>
            <span className={styles.timeRemaining}>
              Recovery in: <strong>{timeRemaining}</strong>
            </span>
            <span className={styles.recoveryDate}>
              (Ready: {mercenary.hospitalizedUntil ? formatDate(mercenary.hospitalizedUntil) : 'Unknown'})
            </span>
          </div>
          <div className={styles.warning}>
            Cannot participate in any battles until recovered
          </div>
        </div>
        <div className={styles.actions}>
          {battleReportId && (
            <Link 
              to={`/battle/result/${battleReportId}`}
              className={styles.battleLink}
            >
              View Battle
            </Link>
          )}
          <Link 
            to={`/hospital/${mercenary.id}`}
            className={styles.hospitalLink}
          >
            Hospital Details
          </Link>
        </div>
      </div>
      {onClose && (
        <button className={styles.closeButton} onClick={onClose}>
          ×
        </button>
      )}
    </div>
  );
};

export default InfirmaryBanner;
